import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  Tooltip,
} from "recharts";

// One Recharts chart per demo panel on the home page ("Votre nouveau copilote du quotidien").
// The chart spec comes from `HomeHelper::DEMOS[i][:chart]` through `data-demo-chart-spec-value`.
type Spec =
  | { type: "bar" | "area"; color: string; data: [string, number][] }
  | { type: "pie"; data: [string, number, string][] };

const TICK = { fontSize: 11, fill: "#64748b" };
const TOOLTIP = { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 };

function DemoChart({ spec }: { spec: Spec }) {
  if (spec.type === "bar") {
    const data = spec.data.map(([label, valeur]) => ({ label, valeur }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={TICK} />
          <Tooltip cursor={{ fill: "rgba(148, 163, 184, 0.12)" }} contentStyle={TOOLTIP} />
          <Bar dataKey="valeur" fill={spec.color} radius={[6, 6, 0, 0]} maxBarSize={36} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (spec.type === "area") {
    const data = spec.data.map(([label, valeur]) => ({ label, valeur }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={TICK} />
          <Tooltip contentStyle={TOOLTIP} />
          <Area
            type="monotone"
            dataKey="valeur"
            stroke={spec.color}
            fill={spec.color}
            fillOpacity={0.2}
            strokeWidth={2.5}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  const data = spec.data.map(([label, valeur, couleur]) => ({ label, valeur, couleur }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <Pie
          data={data}
          dataKey="valeur"
          nameKey="label"
          innerRadius="50%"
          outerRadius="85%"
          paddingAngle={2}
          strokeWidth={0}
          isAnimationActive={false}
        >
          {data.map((entry) => (
            <Cell key={entry.label} fill={entry.couleur} />
          ))}
        </Pie>
        <Tooltip contentStyle={TOOLTIP} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default class extends Controller {
  static values = { spec: Object };

  declare specValue: Spec;
  private root: ReturnType<typeof createRoot> | null = null;

  connect() {
    if (this.root) return;
    this.root = createRoot(this.element as HTMLElement);
    this.root.render(<DemoChart spec={this.specValue} />);
  }

  disconnect() {
    if (!this.root) return;
    this.root.unmount();
    this.root = null;
  }
}
